import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";

interface FamilyManagementProps {
  memberId: string;
  onUpdate?: () => void;
}

export const FamilyManagement = ({ memberId, onUpdate }: FamilyManagementProps) => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [relationships, setRelationships] = useState<any[]>([]);
  const [allMembers, setAllMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState("");
  const [relationType, setRelationType] = useState("");

  useEffect(() => {
    loadRelationships();
    loadAllMembers();
  }, [memberId]);

  const loadRelationships = async () => {
    try {
      // Only load relationships where this member is the subject
      // Reciprocal relationships are automatically created by database trigger
      const { data, error } = await supabase
        .from("family_relationships")
        .select(`
          *,
          related_member:members!family_relationships_related_member_id_fkey(id, full_name)
        `)
        .eq("member_id", memberId);

      if (error) throw error;
      
      console.log("Loaded relationships:", data);
      setRelationships(data || []);
    } catch (error: any) {
      console.error("Error loading relationships:", error);
      toast({
        title: t("member.family.error"),
        description: t("member.family.loadError"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAllMembers = async () => {
    try {
      const { data, error } = await supabase
        .from("members")
        .select("id, full_name")
        .eq("membership_status", "active")
        .neq("id", memberId);

      if (error) throw error;
      
      console.log("Loaded members:", data);
      setAllMembers(data || []);
    } catch (error: any) {
      console.error("Error loading members:", error);
    }
  };

  const handleAddRelationship = async () => {
    if (!selectedMember || !relationType) {
      toast({
        title: t("member.family.error"),
        description: t("member.family.selectError"),
        variant: "destructive",
      });
      return;
    }

    try {
      // First, add the family relationship
      const { error } = await supabase.from("family_relationships").insert({
        member_id: memberId,
        related_member_id: selectedMember,
        relationship_type: relationType,
      });

      if (error) throw error;

      // If relationship is spouse or child, sync their data to family_members
      if (relationType === "spouse" || relationType === "child") {
        // Get the related member's data
        const { data: relatedMemberData, error: fetchError } = await supabase
          .from("members")
          .select("*")
          .eq("id", selectedMember)
          .single();

        if (fetchError) {
          console.error("Error fetching related member data:", fetchError);
        } else if (relatedMemberData) {
          // Check if this exact person is already in family_members
          const { data: existingFamily } = await supabase
            .from("family_members")
            .select("id")
            .eq("member_id", memberId)
            .eq("full_name", relatedMemberData.full_name)
            .maybeSingle();

          if (!existingFamily) {
            // Insert into family_members to display in profile
            const { error: insertError } = await supabase.from("family_members").insert({
              member_id: memberId,
              full_name: relatedMemberData.full_name,
              relationship_type: relationType,
              phone: relatedMemberData.phone,
              email: relatedMemberData.email,
              date_of_birth: relatedMemberData.date_of_birth,
            });
            
            if (insertError) {
              console.error("Error inserting family member:", insertError);
            }
          }
        }
      }

      toast({
        title: t("member.family.success"),
        description: t("member.family.addSuccess"),
      });

      setDialogOpen(false);
      setSelectedMember("");
      setRelationType("");
      loadRelationships();
      loadAllMembers();
      
      // Trigger parent update to refresh profile data
      if (onUpdate) {
        onUpdate();
      }
    } catch (error: any) {
      toast({
        title: t("member.family.error"),
        description: error.message || t("member.family.addError"),
        variant: "destructive",
      });
    }
  };

  const handleDeleteRelationship = async (relationshipId: string) => {
    try {
      const { error } = await supabase
        .from("family_relationships")
        .delete()
        .eq("id", relationshipId);

      if (error) throw error;

      toast({
        title: t("member.family.success"),
        description: t("member.family.removeSuccess"),
      });

      loadRelationships();
      loadAllMembers();
      
      // Trigger parent update to refresh profile data
      if (onUpdate) {
        onUpdate();
      }
    } catch (error: any) {
      toast({
        title: t("member.family.error"),
        description: t("member.family.removeError"),
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>{t("member.family.title")}</CardTitle>
            <CardDescription>{t("member.family.description")}</CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                {t("member.family.addRelationship")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("member.family.addTitle")}</DialogTitle>
                <DialogDescription>
                  {t("member.family.addDescription")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>{t("member.family.churchMember")}</Label>
                  <Select value={selectedMember} onValueChange={setSelectedMember}>
                    <SelectTrigger>
                      <SelectValue placeholder={t("member.family.selectMember")} />
                    </SelectTrigger>
                    <SelectContent className="bg-background border">
                      {allMembers.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground">
                          No members available
                        </div>
                      ) : (
                        allMembers.map((member) => (
                          <SelectItem key={member.id} value={member.id}>
                            {member.full_name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>{t("member.family.relationshipType")}</Label>
                  <Select value={relationType} onValueChange={setRelationType}>
                    <SelectTrigger>
                      <SelectValue placeholder={t("member.family.selectRelationship")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spouse">{t("member.family.spouse")}</SelectItem>
                      <SelectItem value="parent">{t("member.family.parent")}</SelectItem>
                      <SelectItem value="child">{t("member.family.child")}</SelectItem>
                      <SelectItem value="sibling">{t("member.family.sibling")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button onClick={handleAddRelationship} className="w-full">
                  {t("member.family.addButton")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p>{t("member.family.loading")}</p>
        ) : relationships.length === 0 ? (
          <p className="text-muted-foreground">
            {t("member.family.noRelationships")}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("member.family.name")}</TableHead>
                <TableHead>{t("member.family.relationship")}</TableHead>
                <TableHead className="w-[100px]">{t("member.family.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {relationships.map((rel) => (
                <TableRow key={rel.id}>
                  <TableCell>{rel.related_member?.full_name}</TableCell>
                  <TableCell className="capitalize">
                    {rel.relationship_type}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteRelationship(rel.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
